import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button, Card, ConfigProvider, Progress, Table, Tag } from "antd";
import Footer from "../../CoreComponents/Footer";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import { is_superuser, url } from "../../Functionalities/Config";
import { getToken } from "../../Functionalities/Functions";
import {
  DASH, ageText, clockMinutes, clockTime, diskLine, durationText, jobStatus, loadAverages,
  loadLine, loadPercent, mediaLine, num, percentColor, processesLine, runsText, uptimeText,
  usageLine, yesNo,
} from "./format";

const NO_ACCESS = "Only superusers can see server health";

const THEME = {
  token: { colorPrimary: "#fb310d", borderRadius: 8, fontFamily: '"Inter", sans-serif', controlHeight: 36 },
};

const Row = ({ label, children, percent }) => (
  <div className="mb-3">
    <div className="d-flex justify-content-between flex-wrap">
      <strong>{label}</strong>
      <span>{children}</span>
    </div>
    {percent !== undefined && (
      <Progress
        percent={percent === null ? 0 : Math.min(100, Math.round(percent))}
        strokeColor={percentColor(percent)}
        showInfo={false}
        size="small"
      />
    )}
  </div>
);

const Fact = ({ label, children }) => (
  <div className="d-flex justify-content-between flex-wrap mb-2">
    <strong>{label}</strong>
    <span>{children}</span>
  </div>
);

export default function ServerHealth() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const latest = useRef(0);

  const load = useCallback(() => {
    const id = ++latest.current;
    setLoading(true);
    axios
      .get(`${url}/insights/server-health/`, { headers: { Authorization: getToken() } })
      .then((res) => {
        if (id !== latest.current) return;
        setData(res.data);
        setError("");
        setNow(Date.now());
      })
      .catch((err) => {
        if (id !== latest.current) return;
        const status = err.response && err.response.status;
        setError(status === 401 || status === 403 ? NO_ACCESS : "Server health could not be loaded.");
      })
      .finally(() => {
        if (id === latest.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!is_superuser) return undefined;
    load();
    return () => {
      latest.current += 1;
    };
  }, [load]);

  const machine = (data && data.machine) || {};
  const storage = (data && data.storage) || {};
  const app = (data && data.app) || {};
  const jobs = (data && data.jobs) || [];
  const memory = machine.memory || {};
  const swap = machine.swap || {};
  const disk = storage.disk || {};
  const media = storage.media || {};
  const load1 = Array.isArray(machine.load) ? machine.load[0] : null;
  const cpuPercent = loadPercent(load1, machine.cpu_cores);

  const columns = [
    { title: "Job", dataIndex: "label", key: "label" },
    { title: "Runs", key: "runs", render: (_, j) => runsText(j.every_minutes) },
    { title: "Installed", key: "installed", render: (_, j) => yesNo(j.installed) },
    { title: "Python found", key: "python", render: (_, j) => yesNo(j.python_found) },
    {
      title: "Last ran",
      key: "last",
      render: (_, j) => (j.last_ran_at ? ageText(j.last_ran_at, now) : j.installed === null ? DASH : "never"),
    },
    {
      title: "Status",
      key: "status",
      render: (_, j) => {
        const s = jobStatus(j);
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
  ];

  const body = () => {
    if (!is_superuser || error === NO_ACCESS) return <div className="alert alert-warning">{NO_ACCESS}</div>;
    return (
      <>
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div>
            <h3 className="mb-0">Server health</h3>
            <span className="text-muted">{data ? `Updated ${clockTime(data.generated_at)}` : DASH}</span>
          </div>
          <Button onClick={load} loading={loading}>Refresh</Button>
        </div>
        {error && <div className="alert alert-danger mb-3">{error}</div>}
        <div className="row">
          <div className="col-xl-4 col-lg-6">
            <Card title="Machine" className="mb-3">
              <Row label="CPU load" percent={cpuPercent}>{loadLine(machine.load, machine.cpu_cores)}</Row>
              <div className="text-muted mb-3">{loadAverages(machine.load)}</div>
              <Row label="RAM" percent={memory.used_percent === undefined ? null : memory.used_percent}>
                {usageLine(memory.used_mb, memory.total_mb, memory.used_percent, "MB")}
              </Row>
              <Row label="Swap" percent={swap.used_percent === undefined ? null : swap.used_percent}>
                {usageLine(swap.used_mb, swap.total_mb, swap.used_percent, "MB")}
              </Row>
              <Fact label="Uptime">{uptimeText(machine.uptime_seconds)}</Fact>
            </Card>
          </div>
          <div className="col-xl-4 col-lg-6">
            <Card title="Storage" className="mb-3">
              <Row label="Disk" percent={disk.used_percent === undefined ? null : disk.used_percent}>
                {diskLine(disk)}
              </Row>
              <Fact label="Database">{storage.database_mb === null || storage.database_mb === undefined ? DASH : `${num(storage.database_mb)} MB`}</Fact>
              <Fact label="Media">
                {mediaLine(media)}
                {media.measured_at ? `, measured ${clockMinutes(media.measured_at)}` : ""}
              </Fact>
            </Card>
          </div>
          <div className="col-xl-4 col-lg-12">
            <Card title="App" className="mb-3">
              <Fact label="Version">{app.version || DASH}</Fact>
              <Fact label="Commit">{app.commit || DASH}</Fact>
              <Fact label="Running since">
                {app.running_since
                  ? `${clockTime(app.running_since)} for ${durationText(app.running_since, now)}`
                  : DASH}
              </Fact>
              <Fact label="Processes">{processesLine(app.processes)}</Fact>
            </Card>
          </div>
        </div>
        <Card title="Background jobs">
          <Table columns={columns} dataSource={jobs} rowKey="name" pagination={false} scroll={{ x: true }} />
        </Card>
      </>
    );
  };

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Server health"} />
      <NavBar />
      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid" style={{ maxWidth: "1680px", margin: "0 auto", padding: "1.25rem" }}>
          <ConfigProvider theme={THEME}>{body()}</ConfigProvider>
        </div>
      </div>
      <Footer />
    </div>
  );
}
